import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { CustomButton } from "@/components/Button";
import { StyledModal } from "@/components/StyledModal";
import { layoutTokens, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";

export const REFUSE_CONFIRM_TITLE = "このプランナーからのメッセージを\nお断りしますか？";
// Trello #224 の DOD で指定された文言
export const REFUSE_CONFIRM_DESCRIPTION =
  "同じプランナーからのメッセージが以後届かなくなります。訴求内容はメッセージによって異なるため、ご注意ください。";
export const REFUSE_CONFIRM_NOTE = "※お断りは取り消せません。";
export const REFUSE_CONFIRM_SUBMIT_LABEL = "お断りする";
export const REFUSE_CONFIRM_CANCEL_LABEL = "やめる";
// お断り確定後の通知（SP の詳細ページ・PC のペインで共通）
export const MESSAGE_REFUSED_TOAST = "お断りしました";
export const MESSAGE_REFUSE_FAILED_TOAST = "お断りできませんでした。時間をおいて、もう一度お試しください。";
// 404: 既に表示対象外（お断り済み・提携終了・削除済みなど）。再試行しても回復しないので一覧へ戻す
export const MESSAGE_REFUSE_UNAVAILABLE_TOAST = "このメッセージは表示できなくなりました";
// 422: お断りできない状態（マッチ済みなど）。API の message が無いときの既定文言
export const MESSAGE_NOT_REFUSABLE_TOAST = "このメッセージはお断りできません。";

const useStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: layoutTokens.space[16],
    padding: layoutTokens.space[24],
    [theme.breakpoints.up("md")]: { gap: layoutTokens.space[24], padding: layoutTokens.space[32] },
  },
  title: {
    ...textStyles["SP/Heading/H3"],
    color: colors["text/heading"],
    textAlign: "center",
    // 定数内の改行で2行に分けて表示する
    whiteSpace: "pre-line",
  },
  description: {
    ...textStyles["SP/Body/Large"],
    textAlign: "left",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"],
  },
  note: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    width: "100%",
    textAlign: "left",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[8],
    width: "100%",
    [theme.breakpoints.up("md")]: { width: 360, maxWidth: "100%" },
  },
});

interface RefuseConfirmModalProps {
  // お断りを実行する。非同期の場合は完了まで実行ボタンを押せなくする
  onConfirm: () => void | Promise<void>;
  // 何もせず閉じる
  onCancel: () => void;
}

// メッセージ詳細の「お断り」の直前に挟む確認モーダル（Trello #224）。
// お断りは取り消せず、同じプランナーの過去分も含めて見えなくなるため、誤タップでの確定を防ぐのが目的。
// 二重送信の防止は MatchConfirmModal と同じ ref ガード
export const RefuseConfirmModal: React.FC<RefuseConfirmModalProps> = ({ onConfirm, onCancel }) => {
  const classes = useStyles();
  const [isConfirming, setIsConfirming] = React.useState(false);
  const isConfirmingRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleConfirm = React.useCallback(async () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    setIsConfirming(true);
    try {
      await onConfirm();
    } catch {
      // 失敗の通知は呼び出し側の責務。ここで投げ直すと unhandled rejection になるため受け止めるだけにする
    } finally {
      // 失敗しても押せる状態に戻す（成功時は呼び出し側が閉じる）
      isConfirmingRef.current = false;
      if (mountedRef.current) setIsConfirming(false);
    }
  }, [onConfirm]);

  const handleCancel = () => {
    if (!isConfirmingRef.current) onCancel();
  };

  return (
    <StyledModal onClickOutside={handleCancel} role="alertdialog" ariaLabel={REFUSE_CONFIRM_TITLE.replace("\n", "")}>
      <Box className={classes.content} data-testid="refuse-confirm-modal">
        <Typography component="h2" className={classes.title}>
          {REFUSE_CONFIRM_TITLE}
        </Typography>
        <Typography className={classes.description}>{REFUSE_CONFIRM_DESCRIPTION}</Typography>
        <Typography className={classes.note}>{REFUSE_CONFIRM_NOTE}</Typography>
        <Box className={classes.actions}>
          <CustomButton tone="primary" buttonSize="L" fullWidth onClick={handleConfirm} disabled={isConfirming}>
            {REFUSE_CONFIRM_SUBMIT_LABEL}
          </CustomButton>
          <CustomButton tone="text" buttonSize="M" fullWidth onClick={handleCancel} disabled={isConfirming}>
            {REFUSE_CONFIRM_CANCEL_LABEL}
          </CustomButton>
        </Box>
      </Box>
    </StyledModal>
  );
};
